import { useState, useContext } from 'react';
import {
  Button,
  ButtonWidth,
  useTranslation,
  ButtonStyles,
  useSession,
  useNextgenRouter,
} from '@nextgen-web-framework/all';
import { formatPrice, formatNumber } from '@nextgen-web-framework/core';
import { InitialCartListState, InitialCartState, ListResponse } from '@commerce-cart-webcomponents/sdk';
import { useSelector } from 'react-redux';
import type { AppState } from '@nextgen-web-framework/core';
import { AddListToCartDrawer } from '../add-list-to-cart-drawer/AddListToCartDrawer';
import AllCustomListContext from '../../../../common/CustomListContext';
import { useFeatureFlag } from '../../../../common';
import styles from './ListTotal.module.scss';

type ListTotalProps = {
  cartDrawerCB?: (show: boolean) => void;
  registrationFee?: number;
};

export const ListTotal = ({ cartDrawerCB, registrationFee = 0 }: ListTotalProps) => {
  const t = useTranslation();
  const { isABO, isAnonymous } = useSession();
  const [displaylistToCartDrawer, setDisplaylistToCartDrawer] = useState(false);
  const router = useNextgenRouter();
  const userRegFee = isAnonymous() ? registrationFee : 0;
  const { hideBVFlag } = useFeatureFlag();

  const { customList } = useContext(AllCustomListContext);
  const { myList }: InitialCartListState = useSelector((state: AppState) => state.commerceMyList);
  const listId = router.params?.listId ?? '';
  const currentList = listId === myList?.listId ? myList : customList;
  const { cart }: InitialCartState = useSelector((state: AppState) => state.commerceCart) ?? {};
  const showListPrice = () => (
    <div className={styles['myList-items-total-details-total']}>
      {t('cart.myListItems.total').replace(/{{Quantity}}/g, String(currentList?.totalEntries ?? 0))}

      <span className={styles['myList-items-total-details-total-list-price']}>
        {formatPrice((currentList?.totalPrice?.value ?? 0) + userRegFee)}
      </span>
    </div>
  );

  const handleDisplayDrawer = (show: boolean) => {
    setDisplaylistToCartDrawer(show);
    cartDrawerCB?.(show);
  };

  const showAmwayValues = () => (
    <div className={styles['myList-items-total-amway-values']}>
      <span className={styles['myList-items-total-amway-values-label']}>
        {hideBVFlag ? t('cart.cartTotal.pvMobile') : t('cart.cartTotal.pvbvMobile')}
      </span>

      <span className={styles['myList-items-total-amway-values-pv_bv']}>
        {`${formatNumber(currentList.totalPrice?.amwayValue?.pointValue || 0)} ${
          hideBVFlag ? '' : `/ ${formatNumber(currentList.totalPrice?.amwayValue?.businessVolume || 0)}`
        }`}
      </span>
    </div>
  );

  const showAddItemToCartBtn = () => (
    <div className={styles['add-items-to-cart']}>
      <Button
        id="add-items-to-cart"
        title={t('cart.myListItems.addItemsToCart')}
        tabIndex={0}
        onClick={() => handleDisplayDrawer(true)}
        buttonStyle={ButtonStyles.SOLID}
        buttonWidth={ButtonWidth.FULL_WIDTH}
        role="button"
        data-testid="add-items-to-cart-test-id"
        aria-label="add-items-to-cart"
        disabled={currentList?.totalEntries === 0}
      />
    </div>
  );

  return (
    <>
      {displaylistToCartDrawer && (
        <AddListToCartDrawer
          cart={cart}
          listId=""
          myList={currentList as ListResponse}
          handleDrawerClose={() => handleDisplayDrawer(false)}
        />
      )}
      <div className={styles['myList-items-total']}>
        <div className={styles['myList-items-total-desktop']}>
          <div className={styles['myList-items-total-details']}>
            <span className={styles['myList-items-total-details-summary']}>{t('cart.myListItems.summary')}</span>
            <div className={styles['myList-items-total-desktop-combined-values']}>
              {showListPrice()}
              {isABO() && showAmwayValues()}
            </div>
            {showAddItemToCartBtn()}
          </div>
        </div>

        <div className={`${styles['myList-items-total-mobile']} sticky-bottom-bar`}>
          <div>
            {showListPrice()}
            {isABO() && showAmwayValues()}
          </div>
          {showAddItemToCartBtn()}
        </div>

        <div className={`${styles['myList-items-total-tablet']} sticky-bottom-bar`}>
          <div className={styles['myList-items-total-tablet-combined-values']}>
            {showListPrice()}
            {isABO() && showAmwayValues()}
          </div>
          {showAddItemToCartBtn()}
        </div>
      </div>
    </>
  );
};
